/**
 * Booth activity logger — batches events client-side and flushes to the
 * backend every FLUSH_INTERVAL_MS (or immediately for error-level events).
 *
 * Goal: reconstruct every session's flow end-to-end from the admin dashboard,
 * so unexplained crashes ("screen turned white after countdown") become
 * diagnosable without physical booth access.
 *
 * Levels: debug | info | warn | error
 * Categories (free-form but keep them consistent): 'screen', 'camera',
 * 'capture', 'payment', 'ws', 'session', 'error', 'lifecycle', 'input'.
 */

const FLUSH_INTERVAL_MS = 3000;
const MAX_BUFFER = 200;      // hard cap to avoid unbounded memory
const MAX_BATCH = 100;       // per-request cap (backend also caps to 200)

class BoothLogger {
  constructor() {
    this.buffer = [];
    this.timer = null;
    this.kioskId = null;
    this.sessionId = null;
    this.currentScreen = '';
    this.baseUrl = process.env.REACT_APP_BACKEND_URL || '';
  }

  configure({ kioskId, sessionId, screen }) {
    if (kioskId !== undefined) this.kioskId = kioskId;
    if (sessionId !== undefined) this.sessionId = sessionId;
    if (screen !== undefined) this.currentScreen = screen;
  }

  log(level, category, event, message, data) {
    const entry = {
      ts: new Date().toISOString(),
      level,
      category: category || '',
      event: event || '',
      screen: this.currentScreen || '',
      session_id: this.sessionId || '',
      message: message || '',
      data: data === undefined ? null : data,
    };

    // Always mirror to console so venue techs with DevTools open see it too
    const consoleFn =
      level === 'error' ? console.error :
      level === 'warn' ? console.warn : console.log;
    try {
      consoleFn(`[booth] ${level.toUpperCase()} ${category}/${event}: ${message}`, data || '');
    } catch (_e) { /* console can throw on some Electron versions during shutdown */ }

    // v3.10.0 Layer 3: mirror EVERY event to a local file via Electron
    // IPC. Survives renderer crashes because the write happens in the
    // main process, not in the (potentially frozen) renderer. Cheap:
    // fire-and-forget ipcRenderer.send with no round-trip.
    try {
      if (typeof window !== 'undefined' && window.piczooKiosk?.logEventLocal) {
        window.piczooKiosk.logEventLocal(entry);
      }
    } catch (_e) { /* IPC bridge may not be available (dev browser) */ }

    this.buffer.push(entry);
    if (this.buffer.length > MAX_BUFFER) {
      // Drop oldest — never let the buffer grow unbounded
      this.buffer.splice(0, this.buffer.length - MAX_BUFFER);
    }

    // Flush errors immediately so they land in admin even if the renderer
    // crashes right after the log call.
    if (level === 'error') {
      this.flush();
    } else if (!this.timer) {
      this.timer = setTimeout(() => { this.timer = null; this.flush(); }, FLUSH_INTERVAL_MS);
    }
  }

  // Convenience methods
  debug(category, event, message, data) { this.log('debug', category, event, message, data); }
  info(category, event, message, data) { this.log('info', category, event, message, data); }
  warn(category, event, message, data) { this.log('warn', category, event, message, data); }
  error(category, event, message, data) { this.log('error', category, event, message, data); }

  flush() {
    if (this.buffer.length === 0) return;
    if (!this.kioskId || !this.baseUrl) {
      // Not configured yet — keep the buffer, we'll flush once configured
      return;
    }
    const batch = this.buffer.splice(0, MAX_BATCH);
    // Fire and forget — never block the UI on log delivery. `keepalive` lets
    // the request survive a page unload during a crash.
    try {
      fetch(`${this.baseUrl}/api/kiosks/${this.kioskId}/activity-log`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        keepalive: true,
        body: JSON.stringify({ events: batch }),
      }).catch(() => {
        // Failed — re-queue the batch at the front so we retry on next flush.
        // Cap re-queue to avoid runaway growth on persistent backend outage.
        if (this.buffer.length + batch.length <= MAX_BUFFER) {
          this.buffer = [...batch, ...this.buffer];
        }
      });
    } catch (_e) {
      // fetch itself threw — same recovery path
      if (this.buffer.length + batch.length <= MAX_BUFFER) {
        this.buffer = [...batch, ...this.buffer];
      }
    }
  }
}

// Single global instance shared across the app
const boothLogger = new BoothLogger();

// Flush on page unload / visibility change to catch last-second events
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', () => boothLogger.flush());
  window.addEventListener('pagehide', () => boothLogger.flush());
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') boothLogger.flush();
  });
  // Also install a global error handler so uncaught exceptions land in the log
  // even when they escape the ErrorBoundary (e.g. thrown from async callbacks).
  window.addEventListener('error', (e) => {
    try {
      boothLogger.error('error', 'window_error', e.message || 'Unknown error', {
        stack: e.error?.stack || '',
        filename: e.filename,
        lineno: e.lineno,
        colno: e.colno,
      });
    } catch (_ignore) { /* noop */ }
  });
  window.addEventListener('unhandledrejection', (e) => {
    try {
      boothLogger.error('error', 'unhandled_promise', String(e.reason?.message || e.reason || 'Unknown rejection'), {
        stack: e.reason?.stack || '',
      });
    } catch (_ignore) { /* noop */ }
  });
}

export default boothLogger;
